import { useEffect, useRef } from 'react';
import { X, Bell, ChevronRight } from 'lucide-react';
import { cn } from '@/utils';
import { useGetNotificationsQuery, useMarkAllReadMutation } from '@/store/api/notificationApi';
import { NotificationMode, PushNotification } from '@/types/notification';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useNavigate } from 'react-router-dom';

dayjs.extend(relativeTime);

interface NotificationSidebarProps {
  open: boolean;
  onClose: () => void;
}

const MODE_TYPE_MAP: Record<NotificationMode, string> = {
  Upi: 'qr',
  Bank: 'banks',
  Crypto: 'crypto',
};

function getRoute(mode: NotificationMode, type: PushNotification['type']): string {
  return `/requests/${type ?? 'Credit'}/${MODE_TYPE_MAP[mode] ?? 'qr'}`;
}

export function NotificationSidebar({ open, onClose }: NotificationSidebarProps) {
  const navigate = useNavigate();
  const [markAllRead] = useMarkAllReadMutation();
  const markedRef = useRef(false);

  const { data, isLoading } = useGetNotificationsQuery(undefined, {
    skip: !open,
  });

  useEffect(() => {
    if (open && !markedRef.current) {
      markedRef.current = true;
      markAllRead();
    }
    if (!open) {
      markedRef.current = false;
    }
  }, [open, markAllRead]);

  const notifications = data?.notifications ?? [];

  const handleNotifClick = (mode: NotificationMode, type: PushNotification['type']) => {
    navigate(getRoute(mode, type));
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/50 transition-opacity duration-300',
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
      />

      {/* Sidebar panel */}
      <div
        className={cn(
          'fixed top-0 right-0 z-50 h-full w-[340px] flex flex-col transition-transform duration-300',
          'bg-white border-l-2 border-[#EAAD07]/40 shadow-[-4px_0_24px_rgba(7,44,80,0.2)]',
          open ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-header">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-[#EAAD07]" />
            <span className="text-white font-bold text-base tracking-wide">Notifications</span>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white transition-colors rounded-full hover:bg-white/10 p-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Count bar */}
        {!isLoading && notifications.length > 0 && (
          <div className="px-5 py-2 bg-[#F5F7FA] border-b border-[#EAAD07]/20">
            <span className="text-xs text-[#072C50]/70">
              {notifications.filter((n) => !n.read).length > 0
                ? `${notifications.filter((n) => !n.read).length} unread`
                : 'All caught up'}
            </span>
          </div>
        )}

        {/* Notifications list */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-40 gap-3">
              <div className="h-6 w-6 rounded-full border-2 border-[#EAAD07] border-t-transparent animate-spin" />
              <span className="text-[#072C50]/50 text-sm">Loading...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <Bell className="h-10 w-10 text-[#072C50]/20" />
              <span className="text-[#072C50]/50 text-sm">No notifications yet</span>
            </div>
          ) : (
            <ul className="divide-y divide-[#EAAD07]/10">
              {notifications.map((n) => (
                <li key={n.id}>
                  <button
                    onClick={() => handleNotifClick(n.mode, n.type)}
                    className={cn(
                      'w-full text-left px-5 py-4 flex items-start gap-3 transition-colors',
                      'hover:bg-[#F5F7FA] active:bg-[#EAAD07]/10',
                      !n.read && 'bg-[#EAAD07]/5'
                    )}
                  >
                    {/* Unread dot */}
                    <span
                      className={cn(
                        'mt-1.5 h-2 w-2 flex-shrink-0 rounded-full',
                        !n.read ? 'bg-[#EAAD07]' : 'bg-transparent'
                      )}
                    />

                    <div className="flex-1 min-w-0">
                      <p
                        className={cn(
                          'text-sm leading-snug',
                          !n.read ? 'text-[#072C50] font-semibold' : 'text-[#072C50]/80 font-medium'
                        )}
                      >
                        {n.notification}
                      </p>
                      <p className="text-[#EAAD07] text-xs mt-1 font-medium">{n.scrren}</p>
                      <p className="text-[#072C50]/50 text-xs mt-1">
                        {dayjs(n.createdAt).fromNow()}
                      </p>
                    </div>

                    <ChevronRight className="h-4 w-4 text-[#072C50]/30 mt-1 flex-shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
